import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Target, AlertTriangle, Info, Clock, Cpu, Crosshair, Calendar, BookOpen, Megaphone } from 'lucide-react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import SendSignalConfirmModal from './SendSignalConfirmModal';
import { useShallow } from 'zustand/react/shallow';
import { useAnalysisStore } from '../../store/analysisStore';
import { useAuthStore } from '../../store/authStore';
import { formatPrice, ALL_SYMBOLS, getInstrumentTypeFromSymbol } from '@trading/shared';
import type { MethodologyOverlay, AnalysisDirection, Timeframe } from '@trading/shared';

const NARRATIVE_CLAMP_THRESHOLD = 150;

// Get price precision based on symbol
function getSymbolPrecision(symbolId: string): number {
  const found = ALL_SYMBOLS.find(s => s.id === symbolId);
  return found?.pricePrecision ?? 5;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Compact, methodology-aware summary block. Renders one short line per
 * overlay element so the panel reads differently for each methodology
 * (e.g. "Golden Zone: 1.0823-1.0856" for Fibonacci vs. "London Killzone"
 * for ICT). The full visual story still lives on the chart.
 */
function MethodologyOverlaySummary({ overlay, t }: { overlay: MethodologyOverlay; t: TFn }) {
  const items: { label: string; value?: string }[] = [];

  switch (overlay.kind) {
    case 'smc': {
      const obs = overlay.orderBlocks?.length ?? 0;
      const fvgs = overlay.fvg?.length ?? 0;
      const liq = overlay.liquidity?.length ?? 0;
      if (obs) items.push({ label: t('overlays.smc.orderBlock'), value: String(obs) });
      if (fvgs) items.push({ label: t('overlays.smc.fvg'), value: String(fvgs) });
      if (liq) items.push({ label: t('overlays.smc.liquidity'), value: String(liq) });
      if (overlay.bos) items.push({ label: t('overlays.smc.bos') });
      if (overlay.choch) items.push({ label: t('overlays.smc.choch') });
      break;
    }
    case 'ict': {
      overlay.killzones?.forEach((kz) => {
        const key =
          kz.name === 'london'
            ? 'overlays.ict.killzoneLondon'
            : kz.name === 'new-york-am'
            ? 'overlays.ict.killzoneNyAm'
            : kz.name === 'london-close'
            ? 'overlays.ict.killzoneLondonClose'
            : kz.name === 'new-york-pm'
            ? 'overlays.ict.killzoneNyPm'
            : 'overlays.ict.killzoneAsia';
        items.push({ label: t(key) });
      });
      overlay.po3?.forEach((p) => {
        const key =
          p.phase === 'accumulation'
            ? 'overlays.ict.po3Accumulation'
            : p.phase === 'manipulation'
            ? 'overlays.ict.po3Manipulation'
            : 'overlays.ict.po3Distribution';
        items.push({ label: t(key) });
      });
      if (overlay.ote) {
        items.push({
          label: t('overlays.ict.ote'),
          value: `${overlay.ote.bottomPrice.toFixed(5)}–${overlay.ote.topPrice.toFixed(5)}`,
        });
      }
      if (overlay.mss) items.push({ label: t('overlays.ict.mss') });
      break;
    }
    case 'price-action': {
      const pins = overlay.pinBars?.length ?? 0;
      const eng = overlay.engulfings?.length ?? 0;
      const piv = overlay.pivots?.length ?? 0;
      const sup = overlay.supply?.length ?? 0;
      const dem = overlay.demand?.length ?? 0;
      if (pins) items.push({ label: t('overlays.priceAction.pinBar'), value: String(pins) });
      if (eng) items.push({ label: t('overlays.priceAction.engulfing'), value: String(eng) });
      if (piv) items.push({ label: t('overlays.priceAction.swingHigh'), value: String(piv) });
      if (sup) items.push({ label: t('overlays.priceAction.supply'), value: String(sup) });
      if (dem) items.push({ label: t('overlays.priceAction.demand'), value: String(dem) });
      break;
    }
    case 'fibonacci': {
      if (overlay.goldenZone) {
        items.push({
          label: t('overlays.fibonacci.goldenZone'),
          value: `${overlay.goldenZone.lowPrice.toFixed(5)}–${overlay.goldenZone.highPrice.toFixed(5)}`,
        });
      }
      overlay.levels?.forEach((lvl) => {
        items.push({ label: t('overlays.fibonacci.level'), value: `${lvl.ratio.toFixed(3)} @ ${lvl.price.toFixed(5)}` });
      });
      overlay.extensions?.slice(0, 3).forEach((ext) => {
        items.push({ label: t('overlays.fibonacci.extension'), value: `${ext.ratio.toFixed(3)} @ ${ext.price.toFixed(5)}` });
      });
      break;
    }
  }

  if (items.length === 0) return null;

  return (
    <div className="p-4 border-b border-border-subtle">
      <h4 className="text-xs uppercase tracking-wide text-text-muted mb-2">{t('overlays.title')}</h4>
      <ul className="space-y-1">
        {items.slice(0, 6).map((it, idx) => (
          <li key={idx} className="flex items-center justify-between text-xs">
            <span className="text-text-secondary">{it.label}</span>
            {it.value && <span className="font-mono text-text-primary">{it.value}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

export default function AnalysisPanel() {
  const { t, i18n } = useTranslation('analysis');
  const { currentAnalysis, isAnalyzing, error, symbol } = useAnalysisStore(
    useShallow((s) => ({
      currentAnalysis: s.currentAnalysis,
      isAnalyzing: s.isAnalyzing,
      error: s.error,
      symbol: s.symbol,
    }))
  );
  const userRole = useAuthStore((s) => s.user?.role);
  const pricePrecision = getSymbolPrecision(symbol);
  const navigate = useNavigate();
  const [showSendSignalModal, setShowSendSignalModal] = useState(false);
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);

  useEffect(() => {
    setNarrativeExpanded(false);
  }, [currentAnalysis?.id]);

  const handleLogTrade = () => {
    if (!currentAnalysis) return;
    const entryPrice = currentAnalysis.setup?.entry?.price ?? currentAnalysis.entry;
    const directionRaw = currentAnalysis.setup?.direction ?? currentAnalysis.direction;
    const dirLower = directionRaw?.toLowerCase();
    const direction: 'long' | 'short' | undefined =
      dirLower === 'long' || dirLower === 'short' ? dirLower : undefined;
    const symbolName = ALL_SYMBOLS.find((s) => s.id === symbol)?.name ?? symbol;
    const entryTime = (() => {
      if (!currentAnalysis.createdAt) return undefined;
      const d = new Date(currentAnalysis.createdAt);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    })();
    const params = new URLSearchParams();
    params.set('symbol', symbolName);
    params.set('instrumentType', getInstrumentTypeFromSymbol(symbol));
    if (entryPrice != null) params.set('entry', String(entryPrice));
    if (direction) params.set('direction', direction);
    if (entryTime) params.set('entryTime', entryTime);
    if (currentAnalysis.id) params.set('analysisId', currentAnalysis.id);
    navigate(`/journal/trades/new?${params.toString()}`);
  };

  if (error) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertTriangle className="w-12 h-12 text-loss mb-4" />
          <p className="text-text-secondary">{error}</p>
        </div>
      </Card>
    );
  }

  if (!currentAnalysis && !isAnalyzing) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Info className="w-12 h-12 text-text-muted mb-4" />
          <p className="text-text-secondary">
            {t('empty.message')}
          </p>
        </div>
      </Card>
    );
  }

  if (isAnalyzing) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-pulse space-y-4 w-full">
            <div className="h-4 bg-bg-tertiary rounded w-3/4" />
            <div className="h-4 bg-bg-tertiary rounded w-1/2" />
            <div className="h-4 bg-bg-tertiary rounded w-5/6" />
          </div>
        </div>
      </Card>
    );
  }

  const { setup, narrative, analysis, direction } = currentAnalysis!;
  // Handle both direct direction and setup.direction
  const tradeDirection = setup?.direction || direction?.toUpperCase();
  const isLong = tradeDirection === 'LONG' || tradeDirection === 'long';

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="h-full"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card padding="none" className="h-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-border-subtle flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">{t('results.title')}</h3>
              <Badge variant={analysis?.trend === 'bullish' ? 'success' : 'danger'}>
                {analysis?.trend || 'neutral'}
              </Badge>
            </div>
            {/* Analysis timestamp — guard against invalid Date to avoid `Invalid time value`. */}
            {(() => {
              if (!currentAnalysis?.createdAt) return null;
              const created = new Date(currentAnalysis.createdAt);
              if (Number.isNaN(created.getTime())) return null;
              return (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-text-muted">
                  <Calendar className="w-3 h-3" />
                  <span>
                    {created.toLocaleDateString(i18n.language, {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })}{' '}
                    {created.toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Direction & Confidence */}
          <div className="p-4 border-b border-border-subtle flex-shrink-0">
            <div className="flex items-center gap-4">
              <div
                className={`
                  flex items-center justify-center w-12 h-12 rounded-xl
                  ${isLong ? 'bg-profit/20' : 'bg-loss/20'}
                `}
              >
                {isLong ? (
                  <TrendingUp className="w-6 h-6 text-profit" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-loss" />
                )}
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">
                  {tradeDirection || t('results.noSetup')}
                </p>
                <p className="text-sm text-text-secondary">
                  {t('results.confidence', {
                    percent: setup?.confidence || currentAnalysis?.confidence || 0,
                  })}
                </p>
              </div>
            </div>

            {/* Confidence bar */}
            <div className="mt-3 h-2 bg-bg-tertiary rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${setup?.confidence || currentAnalysis?.confidence || 0}%` }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className={`h-full rounded-full ${isLong ? 'bg-profit' : 'bg-loss'}`}
              />
            </div>
          </div>

          {/* Scrollable body — narrative, methodology summary, levels, actions */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Narrative */}
            <div className="p-4 border-b border-border-subtle">
              <p className={`text-sm text-text-secondary leading-relaxed${narrativeExpanded ? '' : ' line-clamp-3'}`}>
                {narrative}
              </p>
              {narrative && narrative.length > NARRATIVE_CLAMP_THRESHOLD && (
                <button
                  onClick={() => setNarrativeExpanded((e) => !e)}
                  className="mt-1 text-xs text-accent-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-1"
                >
                  {narrativeExpanded ? t('results.readLess') : t('results.readMore')}
                </button>
              )}
            </div>

            {/* Methodology overlay summary — shows a one-line preview of the
                method-specific overlay so the panel doesn't read identically
                for SMC, ICT, Price Action, and Fibonacci analyses. */}
            {currentAnalysis?.methodologyOverlay && (
              <MethodologyOverlaySummary overlay={currentAnalysis.methodologyOverlay} t={t} />
            )}

            {/* Levels */}
            {(setup?.valid || currentAnalysis?.entry) && (setup?.entry || currentAnalysis?.entry) && (
              <div className="p-4 space-y-3">
              {/* Entry */}
              <div className="p-3 bg-bg-tertiary rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-accent-primary" />
                    <span className="text-sm text-text-secondary">{t('results.entry')}</span>
                  </div>
                  <span className="font-mono font-semibold text-text-primary">
                    {formatPrice(setup?.entry?.price || currentAnalysis?.entry || 0, pricePrecision)}
                  </span>
                </div>
                {/* Entry type and reasoning */}
                {setup?.entry && (
                  <div className="flex items-start gap-2 pt-2 border-t border-border-subtle">
                    <Crosshair className="w-3.5 h-3.5 text-accent-primary mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-text-muted">
                      <span className="font-medium text-accent-primary uppercase">
                        {setup.entry.type || 'limit'} {t('results.order')}
                      </span>
                      {setup.entry.reasoning && (
                        <p className="mt-1 leading-relaxed">{setup.entry.reasoning}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Stop Loss */}
              {(setup?.stopLoss || currentAnalysis?.stopLoss) && (
                <div className="flex items-center justify-between p-3 bg-loss/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-loss" />
                    <span className="text-sm text-text-secondary">{t('results.stopLoss')}</span>
                  </div>
                  <span className="font-mono font-semibold text-loss">
                    {formatPrice(setup?.stopLoss?.price || currentAnalysis?.stopLoss || 0, pricePrecision)}
                  </span>
                </div>
              )}

              {/* Targets */}
              {setup?.targets && (
                <div className="space-y-2">
                  {(['t1', 't2', 't3'] as const).map((target, index) => {
                    const targetData = setup.targets?.[target];
                    if (!targetData) return null;
                    return (
                      <div
                        key={target}
                        className="flex items-center justify-between p-3 bg-profit/10 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-profit" />
                          <span className="text-sm text-text-secondary">
                            {t('results.target', { index: index + 1 })}
                          </span>
                        </div>
                        <span className="font-mono font-semibold text-profit">
                          {formatPrice(targetData.price, pricePrecision)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Risk/Reward */}
              {(setup?.riskReward || currentAnalysis?.riskReward) && (
                <div className="flex items-center justify-between p-3 bg-accent-primary/10 rounded-lg">
                  <span className="text-sm text-text-secondary">{t('results.riskReward')}</span>
                  <span className="font-mono font-semibold text-accent-primary">
                    1:{(setup?.riskReward || currentAnalysis?.riskReward || 0).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Action buttons */}
              {(() => {
                const canSendSignal =
                  (userRole === 'admin' || userRole === 'expert') &&
                  setup?.valid === true &&
                  setup?.entry?.price != null &&
                  setup?.stopLoss?.price != null;

                return (
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full sm:flex-1"
                      onClick={handleLogTrade}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {t('logTrade')}
                    </Button>
                    {canSendSignal && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full sm:flex-1"
                        onClick={() => setShowSendSignalModal(true)}
                      >
                        <Megaphone className="w-4 h-4 mr-2" />
                        {t('sendSignal.button')}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          </div>

          {/* Analysis Metrics Footer */}
          {(() => {
            // Hide the AI model identifier from regular users — internal detail
            // (admin/expert tooling) that leaks the provider choice and gives
            // no value to traders. Duration stays for everyone (perf signal).
            const isPrivileged = userRole === 'admin' || userRole === 'expert';
            const showAiModel = isPrivileged && !!currentAnalysis?.aiModel;
            if (!currentAnalysis?.durationMs && !showAiModel) return null;
            return (
              <div className="p-3 border-t border-border-subtle bg-bg-tertiary/50 flex-shrink-0">
                <div className="flex items-center justify-between text-xs text-text-muted">
                  {currentAnalysis?.durationMs && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      <span>{formatDuration(currentAnalysis.durationMs)}</span>
                    </div>
                  )}
                  {showAiModel && (
                    <div className="flex items-center gap-1.5">
                      <Cpu className="w-3 h-3" />
                      <span>{currentAnalysis.aiModel}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        </Card>
      </motion.div>

      {showSendSignalModal && currentAnalysis && setup?.entry?.price != null && setup?.stopLoss?.price != null && (
        <SendSignalConfirmModal
          input={{
            asset: symbol,
            timeframe: currentAnalysis.timeframe as Timeframe,
            direction: (setup.direction ?? 'LONG') as AnalysisDirection,
            entryPrice: setup.entry.price,
            stopLoss: setup.stopLoss.price,
            takeProfit1: setup.targets?.t1?.price ?? setup.stopLoss.price,
            takeProfit2: setup.targets?.t2?.price,
            takeProfit3: setup.targets?.t3?.price,
            confidence: setup.confidence ?? currentAnalysis.confidence ?? 0,
            narrative: currentAnalysis.narrative ?? '',
          }}
          pricePrecision={pricePrecision}
          onClose={() => setShowSendSignalModal(false)}
        />
      )}
    </AnimatePresence>
  );
}
